import { isOwnPanel, type PanelId } from './seriesTypes';

export const MIN_PROBABILITY_HEIGHT = 240;
export const DEFAULT_PROBABILITY_HEIGHT = 420;
export const PANEL_GAP = 12;

const FIXED_HEIGHTS: Readonly<Record<string, number>> = {
    clock: 180,
    flow: 140,
    position: 110,
};

const OWN_PANEL_HEIGHT = 120;

export interface PanelBox {
    id: PanelId;
    top: number;
    height: number;
}

export interface StackLayout {
    boxes: PanelBox[];
    totalHeight: number;
}

function smallPanelHeight(id: PanelId): number {
    if (isOwnPanel(id)) return OWN_PANEL_HEIGHT;
    return FIXED_HEIGHTS[id] ?? OWN_PANEL_HEIGHT;
}

/**
 * `stripHeights` are the heights of strips drawn above panels, by panel id
 * (outside the boxes). The probability panel gives up their sum, so the whole
 * stack keeps to the height it is given. A box's `top` is where the panel
 * itself starts: below the strips above it, its own included.
 */
export function stackLayout(
    panelIds: readonly PanelId[],
    availableHeight: number | null,
    stripHeights: Readonly<Record<string, number>> = {},
    /** Lower it only for a screen with no room to spare (a phone on its side in full screen). */
    minProbabilityHeight: number = MIN_PROBABILITY_HEIGHT
): StackLayout {
    const stripOf = (id: PanelId): number => stripHeights[id] ?? 0;
    const stripTotal = panelIds.reduce((sum, id) => sum + stripOf(id), 0);
    const smallTotal = panelIds
        .filter(id => id !== 'probability')
        .reduce((sum, id) => sum + smallPanelHeight(id), 0);
    const gaps = Math.max(0, panelIds.length - 1) * PANEL_GAP;
    const budget =
        availableHeight ??
        DEFAULT_PROBABILITY_HEIGHT + smallTotal + gaps + stripTotal;
    const probabilityHeight = Math.max(
        minProbabilityHeight,
        budget - smallTotal - gaps - stripTotal
    );

    const boxes: PanelBox[] = [];
    let y = 0;
    for (const id of panelIds) {
        const height =
            id === 'probability' ? probabilityHeight : smallPanelHeight(id);
        boxes.push({ id, top: y + stripOf(id), height });
        y += stripOf(id) + height + PANEL_GAP;
    }
    const totalHeight = boxes.length > 0 ? y - PANEL_GAP : 0;
    return { boxes, totalHeight };
}

/**
 * The panels of `foldable` that have to be folded away (drawn as chips, like
 * hidden ones) for the main panel to keep `minProbabilityHeight` beside the
 * rest. `room` is the height the panels may take: their box less the time
 * axis. Panels that are not foldable are never returned, so with only those
 * the stack is as tall as it is and the page scrolls, as before.
 */
export function panelsToFold(
    panelIds: readonly PanelId[],
    room: number,
    stripHeights: Readonly<Record<string, number>>,
    foldable: ReadonlySet<string>,
    minProbabilityHeight: number = MIN_PROBABILITY_HEIGHT
): PanelId[] {
    const candidates = panelIds.filter(id => foldable.has(id));
    if (candidates.length === 0) return [];
    // At its least the main panel is what the layout gives it with no room
    // to spare; the 1 px rule above each later panel is outside the layout.
    const { totalHeight } = stackLayout(
        panelIds,
        0,
        stripHeights,
        minProbabilityHeight
    );
    const rules = Math.max(0, panelIds.length - 1);
    return totalHeight + rules > room ? candidates : [];
}
